/** A refusal from the meet sidecar or MediaService, with its reason code. */
export class SiInviteError extends Error {
    reason;
    status;
    /**
     * Builds one.
     *
     * @param {number} status - The HTTP status.
     * @param {string} reason - The reason code.
     */
    constructor(status, reason) {
        super(reason);
        this.status = status;
        this.reason = reason;
    }
}
/**
 * The room this page is, as its address carries it (what the sidecar marked this member present in).
 *
 * @returns {string}
 */
export function currentRoom() {
    const seg = (window.location.pathname || '').replace(/^\//, '').split('/')[0] || '';
    try {
        return decodeURIComponent(seg);
    }
    catch (e) {
        return seg;
    }
}
/**
 * One call to the sidecar, as the member its cookie names.
 *
 * @param {string} path - The sidecar path.
 * @param {RequestInit} init - Fetch options.
 * @returns {Promise<any>}
 */
async function call(path, init) {
    let res;
    try {
        res = await fetch(path, {
            credentials: 'include',
            headers: { 'Accept': 'application/json',
                'Content-Type': 'application/json' },
            ...init
        });
    }
    catch (e) {
        throw new SiInviteError(0, 'NETWORK');
    }
    let body = null;
    try {
        body = await res.json();
    }
    catch (e) {
        body = null;
    }
    if (!res.ok) {
        throw new SiInviteError(res.status, (body && typeof body.reason === 'string' && body.reason) || `HTTP_${res.status}`);
    }
    return body;
}
/**
 * The SIs this member may bring into the room, and the ones already here.
 *
 * @param {string} room - The room.
 * @returns {Promise<ISiInviteListing>}
 */
export function fetchInvitableSis(room) {
    return call(`/api/meet/si-invites?room=${encodeURIComponent(room)}`).then(b => ({
        meeting: b?.meeting ?? { id: '',
            title: '' },
        present: Array.isArray(b?.present) ? b.present : [],
        sis: Array.isArray(b?.sis) ? b.sis : []
    }));
}
/**
 * Brings this SI into the room now.
 *
 * @param {string} room - The room.
 * @param {string} memberId - The SI.
 * @returns {Promise<Object>} The new state and whether the SI was already there.
 */
export function inviteSi(room, memberId) {
    return call(`/api/meet/si-invites/${encodeURIComponent(memberId)}`, {
        method: 'POST',
        body: JSON.stringify({ room })
    });
}
